import { useCallback, useLayoutEffect, useState } from "react";

import { SCREEN_SM } from "@/style/breakpoints";
import { useResizeObserver } from "@/utils/useResizeObserver";

type WindowEdgeOffsets = {
  left: number;
  right: number;
};

/**
 * Distances from a line's slot in the content column to the window edges.
 * A wide node stretches its container by them so it reaches the window edges,
 * under the sidebars, while its content still starts in the content column.
 *
 * On mobile the left distance is zero: the container stays interactive there
 * (iOS cannot touch-scroll a `pointer-events: none` container), so it must not
 * cover the line number column.
 */
export function useWindowEdgeOffsets(
  container: HTMLElement | null,
  editorElement: HTMLElement,
): WindowEdgeOffsets {
  const [offsets, setOffsets] = useState<WindowEdgeOffsets>({ left: 0, right: 0 });

  const measure = useCallback(() => {
    if (!container) {
      return;
    }

    const { marginLeft, marginRight } = getComputedStyle(container);
    const rect = container.getBoundingClientRect();
    const slotLeft = rect.left - parseFloat(marginLeft);
    const slotRight = rect.right + parseFloat(marginRight);
    const windowWidth = document.documentElement.clientWidth;
    const isMobile = window.innerWidth < SCREEN_SM;

    const next = {
      left: isMobile ? 0 : Math.max(0, Math.floor(slotLeft)),
      right: Math.max(0, Math.floor(windowWidth - slotRight)),
    };

    setOffsets((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  }, [container]);

  useLayoutEffect(() => measure(), [measure]);
  useResizeObserver(container && editorElement, measure);

  return offsets;
}
